import React from "react";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSChip from "../../system-design/DSChip";
import DSCard from "../../system-design/DSCard";
import { Event, Schedule, LocationOn, LocalOffer } from "@mui/icons-material";
import { PopulatedCalendarEvent, State } from "../../../api/calendarEvent";

type TodayEventItemProps = {
  event: PopulatedCalendarEvent;
  date: Date;
  onClick: () => void;
};

const TodayEventItem = ({ event, date, onClick }: TodayEventItemProps) => {
  const formattedDate = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const klydoCount = event.klydos?.length || 0;

  return (
    <DSCard
      sx={{
        p: 3,
        mb: 2,
        cursor: "pointer",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          boxShadow: 4,
          transform: "translateY(-2px)",
        },
      }}
      onClick={onClick}
    >
      <DSBox sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Event color="primary" />
          <DSTypography variant="body2" color="text.secondary">
            {formattedDate}
          </DSTypography>
        </DSBox>
        {klydoCount > 0 && (
          <DSChip
            icon={<Schedule />}
            label={`${klydoCount} Klydo${klydoCount !== 1 ? "s" : ""}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
      </DSBox>

      <DSTypography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
        {event.name}
      </DSTypography>

      <DSBox sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <LocationOn fontSize="small" color="action" />
        <DSTypography variant="body2" color="text.secondary">
          {event.country?.name}
          {event.states &&
            `, ${event.states.map((state: State) => state.name).join(", ")}`}
        </DSTypography>
      </DSBox>

      {event.primaryType && (
        <DSBox sx={{ mb: 1 }}>
          <DSChip
            icon={<LocalOffer />}
            label={event.primaryType}
            size="small"
            color="secondary"
            variant="outlined"
          />
        </DSBox>
      )}

      {event.description && (
        <DSTypography
          variant="body2"
          color="text.secondary"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            mt: 1,
          }}
        >
          {event.description}
        </DSTypography>
      )}

      {klydoCount === 0 && (
        <DSTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No Klydos available for this event
        </DSTypography>
      )}
    </DSCard>
  );
};

export default TodayEventItem;
